import { useCallback, useEffect, useRef } from "react";
import { useConsentService } from "@aviv-csnt/react";
import { useAdsContext } from "@/ads/context";
import { filterAdSlots, createSlotRenderEndedListener } from "../utils/utils";
import { useFarbwelt } from "@/hooks";
import { useBrowserAPI } from "@/hooks/useBrowserAPI";
import { useIsAdLeaderboardHidden } from "@/components/contacting/hooks/useIsAdLeaderboardHidden";
import { useIsGPTAvailable } from "./useIsGPTAvailable";
/**  initialize google ads and define slots
 * 1. wait for GPT to be ready
 * 2. setup GPT
 * 3. define slots
 * 4. set targeting
 * 5. enable services
 * @returns void
 *
 */
export const useGoogleAdsInit = () => {
    const { adSlots, targeting, registerSlot, setGptInitialized, setLoadedSlot, hasDeviceTypeChanged, userDevice, loadedSlots, deleteAllSlots, } = useAdsContext();
    const isInitialized = useRef(false);
    const deviceTypeChanged = useRef(undefined); // to check if device type has changed only once
    const retry = useRef(0); // retry counter for checking if GPT is ready
    const limit = useRef(350); // retry limit for checking if GPT is ready
    const { isFarbweltActive } = useFarbwelt();
    const { bWindow } = useBrowserAPI();
    const isGPTAvailable = useIsGPTAvailable();
    const { isConsentGranted } = useConsentService();
    const isAdLeaderboardHidden = useIsAdLeaderboardHidden();
    /**
     * set targeting for the ad
     * if slot is provided, set targeting on slot level
     * else set targeting on pubads level
     * @param targeting
     * @param slot
     */
    const setTargeting = (targeting, slot) => {
        Object.entries(targeting).forEach(([key, value]) => {
            // targeting can be done on slot level or on pubads level
            if (slot) {
                slot.setTargeting(key, Array.isArray(value) ? value : String(value));
            }
            else {
                googletag
                    .pubads()
                    .setTargeting(key, Array.isArray(value) ? value : String(value));
            }
        });
    };
    const defineSlots = useCallback(() => {
        const usedAdslots = filterAdSlots(adSlots, {
            isFarbweltActive,
            isAdLeaderboardHidden,
        });
        // Define the slots using googletag
        usedAdslots.forEach((ad) => {
            var _a;
            const slot = (_a = googletag
                .defineSlot(ad.adUnit, ad.size, ad.id)) === null || _a === void 0 ? void 0 : _a.addService(googletag.pubads());
            // Add slot-level targeting if available
            if (ad.specificTargeting) {
                setTargeting(ad.specificTargeting, slot);
            }
            // Keep track of this slot
            if (slot) {
                registerSlot(slot.getSlotElementId(), slot);
            }
        });
        // Listen for ad render events to track loaded slots
        const onSlotRenderEnded = createSlotRenderEndedListener(setLoadedSlot);
        googletag.pubads().addEventListener("slotRenderEnded", onSlotRenderEnded);
    }, [
        adSlots,
        isFarbweltActive,
        isAdLeaderboardHidden,
        registerSlot,
        setLoadedSlot,
    ]);
    const displayAndRefreshSlots = useCallback(() => {
        // Display and refresh all loaded slots
        var _a, _b;
        Object.keys(loadedSlots).forEach((slotId) => {
            var _a;
            (_a = bWindow.googletag) === null || _a === void 0 ? void 0 : _a.display(slotId);
        });
        (_b = (_a = bWindow.googletag) === null || _a === void 0 ? void 0 : _a.pubads) === null || _b === void 0 ? void 0 : _b.call(_a).refresh();
    }, [loadedSlots, bWindow]);
    // Setup and initialize Google Publisher Tags (GPT)
    const initializeGPT = useCallback(() => {
        googletag.cmd.push(() => {
            // Set global targeting parameters
            setTargeting(targeting);
            // Enable lazy loading for ads
            // This will load ads only when they are needed
            // This helps improve page load performance
            googletag.pubads().enableLazyLoad({
                fetchMarginPercent: 200, // 2 viewports
                renderMarginPercent: 75, // 0.75 viewports
                mobileScaling: 1.0, // 1.0 same on mobile or desktop
            });
            defineSlots();
            // Configure GPT services
            // Enable single request mode for better performance
            // This allows multiple ad slots to be requested in a single call
            // instead of separate calls for each slot
            googletag.pubads().enableSingleRequest();
            // Track slot render results
            // Enable the GPT services
            googletag.enableServices();
        });
    }, [targeting, defineSlots]);
    const hasTriggeredSlotReset = useRef(false);
    // Trigger cleanup on device change
    useEffect(() => {
        // If the device type has changed, reset the slots
        if (hasDeviceTypeChanged &&
            deviceTypeChanged.current !== userDevice &&
            isGPTAvailable()) {
            googletag.cmd.push(() => {
                googletag.destroySlots();
                deleteAllSlots(); //  empty loadedSlots
            });
            hasTriggeredSlotReset.current = true;
            // @ts-ignore
            deviceTypeChanged.current = userDevice;
        }
    }, [hasDeviceTypeChanged, userDevice, deleteAllSlots, isGPTAvailable]);
    // Wait until loadedSlots is empty, then define & display
    useEffect(() => {
        if (hasTriggeredSlotReset.current &&
            Object.keys(loadedSlots).length === 0) {
            googletag.cmd.push(() => {
                defineSlots();
                displayAndRefreshSlots();
            });
            hasTriggeredSlotReset.current = false; // reset the trigger
        }
    }, [loadedSlots, defineSlots, displayAndRefreshSlots]);
    const waitForGPT = useCallback((onReady) => {
        const tryInitialize = () => {
            if (isInitialized.current)
                return;
            if (isGPTAvailable() && adSlots.length > 0) {
                isInitialized.current = true;
                setGptInitialized(true);
                onReady();
            }
            else if (retry.current < limit.current) {
                retry.current++;
                setTimeout(tryInitialize, 100);
            }
            else {
                return;
            }
        };
        tryInitialize();
    }, [setGptInitialized, isInitialized, retry, limit, adSlots, isGPTAvailable]);
    useEffect(() => {
        var _a;
        if (!isConsentGranted)
            return;
        //method not available in aviv-csnt package
        //temporary fix, will be deprecated in UC_UI v3
        if (isInitialized.current || !((_a = bWindow === null || bWindow === void 0 ? void 0 : bWindow.UC_UI) === null || _a === void 0 ? void 0 : _a.areAllConsentsAccepted()))
            return;
        waitForGPT(initializeGPT);
    }, [waitForGPT, initializeGPT, isInitialized, isConsentGranted, bWindow]);
};
